import { createActionsHook, createContainer, createHook, createStore } from 'react-sweet-state';

import { previewPanelsActions } from './actions';
import { initialState } from './constants';
import type { Panel, PreviewPanelsApiState } from './types';

/**
 * __Preview panels store__
 *
 * A store that manages the state of preview panels across the application.
 * Handles panel lifecycle events, dimensions, and content management.
 *
 * Features:
 * - Panel opening and closing
 * - Panel width management
 * - Panel content and data storage
 * - Lifecycle event callbacks (onOpen, onClose)
 *
 * - [Examples](https://atlassian.design/components/preview-panels/examples)
 * - [Code](https://atlassian.design/components/preview-panels/code)
 */
export const PreviewPanelsStore = createStore({
	initialState,
	actions: previewPanelsActions,
	name: 'PreviewPanelsStore',
});

/**
 * __Preview panel context container__
 *
 * A context container that manages the state for preview panels across the application.
 * Provides access to panel data, dimensions, and panel lifecycle methods.
 *
 * - [Examples](https://atlassian.design/components/preview-panels/examples)
 * - [Code](https://atlassian.design/components/preview-panels)
 */
export const PreviewPanelsContextContainer = createContainer(PreviewPanelsStore);

export const usePreviewPanelsContext = createHook(PreviewPanelsStore);

export const usePreviewPanelsActions = createActionsHook(PreviewPanelsStore);

export const useIsPanelOpen = createHook(PreviewPanelsStore, {
	selector: (state: PreviewPanelsApiState, panelId?: string): boolean =>
		panelId ? state.panel.id === panelId : !!state.panel.id,
});

export const useCurrentPanel = createHook(PreviewPanelsStore, {
	selector: (state: PreviewPanelsApiState): Panel => state.panel,
});
